import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { Link, NavLink } from 'react-router-dom';
import { useI18n } from '../i18n';
import { FaBars, FaTimes } from 'react-icons/fa';
import { Container as BaseContainer } from './GlobalStyle';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.colors.cardBg};
  padding: 0.75rem 2rem;
  z-index: 1000;
  transition: box-shadow ${({ theme }) => theme.transition.base};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme, $scrolled }) => ($scrolled ? theme.shadow.sm : 'none')};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0.75rem 1rem;
  }
`;

const Nav = styled(BaseContainer).attrs({ as: 'nav' })`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LogoLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: 4px;
`;

const LogoImg = styled.img`
  height: 28px;
`;

const navItemStyles = css`
  color: ${({ theme }) => theme.colors.text};
  text-decoration: none;
  font-weight: 600;
  border-radius: 4px;
  transition: color ${({ theme }) => theme.transition.base};

  &:hover {
    color: ${({ theme }) => theme.colors.brand};
  }
  &.active {
    color: ${({ theme }) => theme.colors.brand};
  }
`;

const NavItem = styled(NavLink)`
  ${navItemStyles}
`;

const DisabledItem = styled.span`
  color: ${({ theme }) => theme.colors.muted};
  font-weight: 600;
  opacity: 0.6;
  cursor: default;
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 1.6rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const LangSwitch = styled.div`
  display: flex;
  gap: 0.4rem;
  align-items: center;
  margin-left: 1rem;
`;

const LangButton = styled.button`
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.brand : theme.colors.border)};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.brand : theme.colors.cardBg};
  color: ${({ theme, $active }) => ($active ? '#fff' : theme.colors.text)};
  padding: 0.25rem 0.55rem;
  border-radius: ${({ theme }) => theme.radii.sm}px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  transition: background ${({ theme }) => theme.transition.base},
    color ${({ theme }) => theme.transition.base},
    border-color ${({ theme }) => theme.transition.base};

  &:hover {
    border-color: ${({ theme }) => theme.colors.brand};
    color: ${({ theme, $active }) => ($active ? '#fff' : theme.colors.brand)};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.focusRing};
  }
`;

const MobileToggle = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  padding: 0.5rem;
  border-radius: ${({ theme }) => theme.radii.sm}px;

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.focusRing};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: block;
  }
`;

const MobileMenu = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    background: ${({ theme }) => theme.colors.cardBg};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    padding: 0.5rem 1.5rem 1.25rem;
    box-shadow: ${({ theme }) => theme.shadow.md};
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    transform: translateY(${({ $open }) => ($open ? '0' : '-8px')});
    visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
    transition: opacity ${({ theme }) => theme.transition.base},
      transform ${({ theme }) => theme.transition.base},
      visibility ${({ theme }) => theme.transition.base};

    ${NavItem}, ${DisabledItem} {
      padding: 1rem 0;
      border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    }
  }
`;

const MobileLangRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const Header = () => {
  const { t, lang, setLang } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMobile = () => setMobileOpen(false);

  const navItems = (
    <>
      <NavItem to="/solutions" onClick={closeMobile}>{t('nav.solutions')}</NavItem>
      <NavItem to="/about" onClick={closeMobile}>{t('nav.about')}</NavItem>
      <DisabledItem aria-disabled="true">{t('nav.team')}</DisabledItem>
      <NavItem to="/updates" onClick={closeMobile}>{t('nav.updates')}</NavItem>
      <NavItem to="/contact" onClick={closeMobile}>{t('nav.contact')}</NavItem>
    </>
  );

  const langButtons = (afterSelect) => (
    <>
      <LangButton
        type="button"
        $active={lang === 'es'}
        aria-pressed={lang === 'es'}
        onClick={() => { setLang('es'); if (afterSelect) afterSelect(); }}
      >
        ES
      </LangButton>
      <LangButton
        type="button"
        $active={lang === 'en'}
        aria-pressed={lang === 'en'}
        onClick={() => { setLang('en'); if (afterSelect) afterSelect(); }}
      >
        EN
      </LangButton>
    </>
  );

  return (
    <HeaderContainer $scrolled={scrolled}>
      <Nav aria-label="Main">
        <LogoLink to="/" onClick={closeMobile}>
          <LogoImg src="/berrik-signature.png" alt="Berrik" />
        </LogoLink>
        <NavLinks>
          {navItems}
          <LangSwitch>{langButtons()}</LangSwitch>
        </NavLinks>
        <MobileToggle
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-expanded={mobileOpen}
          aria-controls="mobile-menu"
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        >
          {mobileOpen ? <FaTimes aria-hidden="true" /> : <FaBars aria-hidden="true" />}
        </MobileToggle>
      </Nav>
      <MobileMenu id="mobile-menu" $open={mobileOpen}>
        {navItems}
        <MobileLangRow>{langButtons(closeMobile)}</MobileLangRow>
      </MobileMenu>
    </HeaderContainer>
  );
};

export default Header;
